import React, { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { Button, Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';
import CreateMultiView from './CreateMultiView';
import EditMultiView from './EditMultiView';
import Search from '../../assets/images/svg/Search_SoS.svg';
import SearchIcon from '../../assets/images/svg/search_iconSos.svg';
import Filter from '../../assets/images/svg/Filter_SoS.svg';
import EditIcon from '../../assets/images/svg/editLearning.svg';
import ShareIcon from '../../assets/images/svg/publishLearning.svg';
import ArchiveIcon from '../../assets/images/svg/archiveLearning.svg';
import ArchiveEnabled from '../../assets/images/svg/archiveEnabled.svg';
import { Trash2 } from 'react-feather';

const tableStyles = `
  .rdt_TableCell { padding: 4px 2px !important; }
  .rdt_TableHead { padding: 4px 2px !important; }
  .rdt_TableCol { padding: 4px 2px !important; }
  .rdt_TableCell[data-column-id="mv-action"] {
    position: sticky !important; right: 0 !important;
    background-color: inherit !important; z-index: 1;
  }
  .rdt_TableHeadRow [data-column-id="mv-action"] {
    position: sticky !important; right: 0 !important;
    background-color: inherit !important; z-index: 1;
  }
  .btn-link,
  .btn-link:hover,
  .btn-link:focus,
  .btn-link:active,
  .btn-link:focus-visible {
    background-color: transparent !important;
    color: inherit !important;
    box-shadow: none !important;
    text-decoration: none !important;
    border-color: transparent !important;
  }
  .btn[title]:hover:after {
    content: attr(title);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    padding: 4px 8px;
    color: white;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 10;
  }
  .btn[title] { position: relative; }
  .btn[disabled] { cursor: not-allowed; }
`;

const cellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: '4px 2px',
    fontSize: '14px',
};

const formatDate = (val) => {
    if (!val) return 'N/A';
    const d = new Date(val);
    if (isNaN(d)) return 'N/A';
    return d.toLocaleDateString('en-GB');
};

const StatusBadge = ({ status }) => {
    const config = {
        active: { label: 'Active', bg: '#4CAF50' },
        inactive: { label: 'Inactive', bg: '#9E9E9E' },
    }[status] || { label: status || 'Unknown', bg: '#9E9E9E' };
    return (
        <span style={{
            minWidth: '70px',
            textAlign: 'center',
            borderRadius: '20px',
            padding: '4px 10px',
            backgroundColor: config.bg,
            color: '#FFFFFF',
            fontSize: '11px',
            fontWeight: '600',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
            {config.label}
        </span>
    );
};

const MultiViewTable = () => {
    const [data, setData] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editGroupId, setEditGroupId] = useState(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: null, title: '' });

    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusTarget, setStatusTarget] = useState({ id: null, title: '', nextStatus: '' });

    const [showSearch, setShowSearch] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const searchRef = useRef(null);

    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const dropdownRef = useRef(null);

    const filterOptions = [
        { label: 'All', value: 'all' },
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) setShowSearch(false);
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchGroups = async () => {
        try {
            setLoading(true);
            const response = await _axios.get(`${ServerPath}/live-darshan/multi-view/list`);
            if (response.data.success === 1) {
                const rows = (response.data.data || []).map(item => ({
                    id: item.id,
                    title: item.title,
                    status: item.status,
                    member_count: item.memberCount,
                    created_at: formatDate(item.createdAt),
                    updated_at: formatDate(item.updatedAt),
                    original_created_at: item.createdAt ? new Date(item.createdAt) : null,
                }));
                setData(rows);
                setOriginalData(rows);
                setTotalRows(rows.length);
            } else {
                toast.error(response.data.message || 'Failed to fetch Multi View groups');
            }
        } catch (error) {
            console.error('Error fetching Multi View groups:', error);
            toast.error(error.response?.data?.message || 'Error fetching Multi View groups');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, []);

    const applyFiltersAndSearch = (search, filter) => {
        let filtered = [...originalData];
        if (search && search.trim()) {
            const term = search.toLowerCase();
            filtered = filtered.filter(item => String(item.title || '').toLowerCase().includes(term));
        }
        if (filter && filter !== 'all') {
            filtered = filtered.filter(item => item.status === filter);
        }
        setData(filtered);
    };

    const handleSearch = (value) => {
        setSearchValue(value);
        applyFiltersAndSearch(value, selectedFilter);
    };

    const handleFilterChange = (value) => {
        setSelectedFilter(value);
        applyFiltersAndSearch(searchValue, value);
        setShowDropdown(false);
    };

    const handleEdit = (id) => {
        setEditGroupId(id);
        setShowEdit(true);
    };

    const handleDeleteClick = (row) => {
        setDeleteTarget({ id: row.id, title: row.title });
        setShowDeleteModal(true);
    };

    const handleStatusClick = (row, nextStatus) => {
        setStatusTarget({ id: row.id, title: row.title, nextStatus });
        setShowStatusModal(true);
    };

    const handleStatusConfirm = async () => {
        try {
            const response = await _axios.post(`${ServerPath}/live-darshan/multi-view/update`, {
                group_id: statusTarget.id,
                status: statusTarget.nextStatus,
            });
            if (response.data.success === 1) {
                toast.success(
                    statusTarget.nextStatus === 'active'
                        ? 'Multi View group marked active'
                        : 'Multi View group archived'
                );
                fetchGroups();
            } else {
                toast.error(response.data.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating Multi View status:', error);
            toast.error(error.response?.data?.message || 'Something went wrong');
        } finally {
            setShowStatusModal(false);
            setStatusTarget({ id: null, title: '', nextStatus: '' });
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            const response = await _axios.post(`${ServerPath}/live-darshan/multi-view/delete`, {
                group_id: deleteTarget.id,
            });
            if (response.data.success === 1) {
                toast.success(response.data.message || 'Multi View group deleted successfully');
                fetchGroups();
            } else {
                toast.error(response.data.message || 'Failed to delete Multi View group');
            }
        } catch (error) {
            console.error('Error deleting Multi View group:', error);
            toast.error(error.response?.data?.message || 'Something went wrong');
        } finally {
            setShowDeleteModal(false);
            setDeleteTarget({ id: null, title: '' });
        }
    };

    const handleCreateClose = () => {
        setShowCreate(false);
        fetchGroups();
    };

    const handleEditClose = () => {
        setShowEdit(false);
        setEditGroupId(null);
        fetchGroups();
    };

    const handlePageChange = (page) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const tableColumns = [
        {
            name: 'Title',
            center: true,
            selector: row => row.title,
            sortable: true,
            width: '320px',
            style: cellStyle,
            cell: row => (
                <div style={{ ...cellStyle, color: '#5662F6', fontWeight: '500', width: '100%', textAlign: 'center' }}>
                    {row.title}
                </div>
            ),
        },
        {
            name: 'Members',
            center: true,
            selector: row => row.member_count,
            sortable: true,
            width: '120px',
            style: cellStyle,
            cell: row => (
                <span style={{
                    minWidth: '40px',
                    display: 'inline-block',
                    backgroundColor: '#EEF2FF',
                    color: '#4338CA',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '4px 10px',
                    borderRadius: '20px',
                }}>
                    {row.member_count}
                </span>
            ),
        },
        {
            name: 'Status',
            center: true,
            selector: row => row.status,
            sortable: true,
            width: '120px',
            style: cellStyle,
            cell: row => <StatusBadge status={row.status} />,
        },
        {
            name: 'Created',
            center: true,
            selector: row => row.created_at,
            sortable: true,
            width: '140px',
            style: cellStyle,
        },
        {
            name: 'Updated',
            center: true,
            selector: row => row.updated_at,
            sortable: true,
            width: '140px',
            style: cellStyle,
        },
        {
            name: 'Action',
            center: true,
            id: 'mv-action',
            width: '220px',
            style: cellStyle,
            cell: row => {
                const isActive = row.status === 'active';
                const isArchived = row.status === 'inactive';
                const iconBtnStyle = {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                };
                return (
                    <div className="d-flex justify-content-center align-items-center gap-2">
                        <button
                            className="btn btn-link p-0"
                            title="Edit"
                            onClick={(e) => { e.stopPropagation(); handleEdit(row.id); }}
                            style={iconBtnStyle}
                        >
                            <img src={EditIcon} alt="edit" style={{ width: '24px', height: '24px' }} />
                        </button>
                        <button
                            className="btn btn-link p-0"
                            title={isActive ? 'Active' : 'Mark Active'}
                            disabled={isActive}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isActive) handleStatusClick(row, 'active');
                            }}
                            style={{ ...iconBtnStyle, opacity: isActive ? 0.5 : 1 }}
                        >
                            <img src={ShareIcon} alt="active" style={{ width: '24px', height: '24px' }} />
                        </button>
                        <button
                            className="btn btn-link p-0"
                            title={isArchived ? 'Archived' : 'Archive'}
                            disabled={isArchived}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isArchived) handleStatusClick(row, 'inactive');
                            }}
                            style={{ ...iconBtnStyle, opacity: isArchived ? 0.5 : 1 }}
                        >
                            <img src={isArchived ? ArchiveEnabled : ArchiveIcon} alt="archive" style={{ width: '24px', height: '24px' }} />
                        </button>
                        <button
                            className="btn btn-link p-0"
                            title="Delete"
                            onClick={(e) => { e.stopPropagation(); handleDeleteClick(row); }}
                            style={{
                                ...iconBtnStyle,
                                backgroundColor: '#FFEBEE',
                                borderRadius: '6px',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            <Trash2 size={18} color="#E53935" />
                        </button>
                    </div>
                );
            },
        },
    ];

    return (
        <Fragment>
            <style>{tableStyles}</style>
            <Container fluid={true}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="mb-0">Multi View Management</h3>
                    <div className="d-flex align-items-center gap-3">
                        <div className="position-relative" ref={searchRef}>
                            <button className="bg-transparent border-0" onClick={() => setShowSearch(true)}>
                                <img src={Search} alt="search" style={{ width: '36px', height: '36px' }} />
                            </button>
                            {showSearch && (
                                <div style={{ position: 'absolute', top: '45px', left: '-180px', width: '220px', zIndex: 1000 }}>
                                    <div style={{
                                        background: 'white',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        padding: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}>
                                        <div style={{
                                            flex: 1, display: 'flex', alignItems: 'center',
                                            background: '#F8F7FA', borderRadius: '6px', padding: '6px 12px',
                                        }}>
                                            <input
                                                type="text"
                                                placeholder="Search Here..."
                                                value={searchValue}
                                                onChange={(e) => handleSearch(e.target.value)}
                                                style={{
                                                    width: '100%', border: 'none', background: 'transparent',
                                                    fontSize: '14px', color: '#666', outline: 'none',
                                                }}
                                                autoFocus
                                            />
                                            <button
                                                style={{
                                                    background: 'none', border: 'none', padding: 0,
                                                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                                                }}
                                                onClick={() => handleSearch(searchValue)}
                                            >
                                                <img src={SearchIcon} alt="search" style={{ width: '20px', height: '20px' }} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="position-relative" ref={dropdownRef}>
                            <button className="bg-transparent border-0" onClick={() => setShowDropdown(!showDropdown)}>
                                <img src={Filter} alt="filter" style={{ width: '36px', height: '36px' }} />
                            </button>
                            {showDropdown && (
                                <div className="position-absolute end-0 mt-2 bg-white rounded shadow p-2"
                                    style={{ width: '180px', zIndex: 1000 }}>
                                    <Form>
                                        {filterOptions.map(option => (
                                            <Form.Check
                                                key={option.value}
                                                type="checkbox"
                                                id={`mv-filter-${option.value}`}
                                                label={option.label}
                                                checked={selectedFilter === option.value}
                                                onChange={() => handleFilterChange(option.value)}
                                            />
                                        ))}
                                    </Form>
                                </div>
                            )}
                        </div>

                        <Button variant="primary" onClick={() => setShowCreate(true)} className="bg-primary">
                            Create Multi View
                        </Button>
                    </div>
                </div>

                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody>
                                <DataTable
                                    columns={tableColumns}
                                    data={data}
                                    pagination
                                    paginationServer={false}
                                    paginationTotalRows={totalRows}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    progressPending={loading}
                                    highlightOnHover
                                    responsive
                                    noDataComponent={<div style={{ padding: '30px', color: '#A0AEC0' }}>No Multi View groups yet.</div>}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {showCreate && <CreateMultiView show={showCreate} onHide={handleCreateClose} />}
            {showEdit && editGroupId && (
                <EditMultiView
                    key={editGroupId}
                    show={showEdit}
                    onHide={handleEditClose}
                    groupId={editGroupId}
                />
            )}

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Body className="text-center p-4">
                    <div className="mb-4">
                        <div className="rounded-circle bg-warning bg-opacity-10 d-inline-flex p-4 mb-3">
                            <i className="bi bi-exclamation-lg fs-4 text-warning"></i>
                        </div>
                        <h4>Delete Multi View Group</h4>
                        <p className="text-secondary mb-0">
                            Delete Multi View group '{deleteTarget.title}'? The streams will stay, but won't appear together as Multi View any more.
                        </p>
                    </div>
                    <div className="d-flex gap-2 justify-content-center">
                        <Button variant="primary" className="px-4" onClick={handleDeleteConfirm}>Yes, Delete</Button>
                        <Button variant="danger" className="px-4" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                <Modal.Body className="text-center p-4">
                    <div className="mb-4">
                        <div className="rounded-circle bg-warning bg-opacity-10 d-inline-flex p-4 mb-3">
                            <i className="bi bi-exclamation-lg fs-4 text-warning"></i>
                        </div>
                        <h4>Are you sure?</h4>
                        <p className="text-secondary mb-0">
                            {statusTarget.nextStatus === 'active'
                                ? `Mark '${statusTarget.title}' as Active? It will be visible in the app.`
                                : `Archive '${statusTarget.title}'? It will be hidden from the app but its configuration is preserved.`}
                        </p>
                    </div>
                    <div className="d-flex gap-2 justify-content-center">
                        <Button variant="primary" className="px-4" onClick={handleStatusConfirm}>Yes</Button>
                        <Button variant="danger" className="px-4" onClick={() => setShowStatusModal(false)}>Cancel</Button>
                    </div>
                </Modal.Body>
            </Modal>
        </Fragment>
    );
};

export default MultiViewTable;
